import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { DialogRef, type DialogConfig } from '@angular/cdk/dialog';
import { DatePipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import type { TranslatableText } from '@zag/core/common';
import { TooltipDirective, TranslatedPipe, ZagButton, ZagIcon } from '@zag/ui';
import type { FeatureView } from '../feature-access-client.service';

export interface ActiveLicenceView {
  readonly key: string;
  readonly name: string;
  readonly description: string | null;
  readonly translations?: TranslatableText<'name' | 'description'>;
  readonly validFrom: string | null;
  readonly validTo: string | null;
  readonly features: readonly FeatureView[];
}

export interface ActiveLicencesDialogData {
  readonly licences: readonly ActiveLicenceView[];
  readonly loading?: boolean;
}

const EXPIRY_SOON_THRESHOLD_MS = 30 * 24 * 60 * 60 * 1000;

export const ACTIVE_LICENCES_DIALOG_CONFIG = {
  width: 'min(var(--dialog-width-max-wide), calc(100vw - (2 * var(--dialog-horizontal-padding))))',
  minWidth: 'min(var(--dialog-width-min-wide), calc(100vw - (2 * var(--dialog-horizontal-padding))))',
  maxWidth: 'calc(100vw - (2 * var(--dialog-horizontal-padding)))',
  maxHeight: 'var(--dialog-max-height-wide)',
  panelClass: 'active-licences-dialog-panel',
  hasBackdrop: true,
  backdropClass: 'ai-chat-dialog-backdrop',
} satisfies Pick<DialogConfig, 'width' | 'minWidth' | 'maxWidth' | 'maxHeight' | 'panelClass' | 'hasBackdrop' | 'backdropClass'>;

@Component({
  selector: 'zag-active-licences-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe, DatePipe, ZagIcon, TooltipDirective, TranslatedPipe, ZagButton],
  template: `
    <div class="licences-dialog" role="dialog" aria-labelledby="licences-dialog-title">
      <div class="licences-dialog__header">
        <h2 id="licences-dialog-title" class="licences-dialog__title">
          {{ 'settings.licensing.dialog.title' | translate }}
        </h2>
        <button
          zagButton
          type="button"
          class="licences-dialog__close"
          (click)="onClose()"
          [attr.aria-label]="'settings.licensing.dialog.close' | translate"
        >
          <span zagIcon="close"></span>
        </button>
      </div>

      <div class="licences-dialog__body">
        @if (loading()) {
          <p class="licences-dialog__loading">{{ 'settings.licensing.dialog.loading' | translate }}</p>
        } @else if (licences().length === 0) {
          <div class="licences-dialog__empty">
            <span zagIcon="card_membership" aria-hidden="true"></span>
            <p>{{ 'settings.licensing.dialog.empty' | translate }}</p>
          </div>
        } @else {
          <ul class="licence-row-list">
            @for (licence of licences(); track licence.key) {
              @let localizedName = (licence.translations | zagTranslated: 'name' : licence.name) ?? licence.name;
              @let localizedDescription = licence.translations | zagTranslated: 'description' : licence.description;
              <li class="licence-row" [class.licence-row--expires-soon]="isExpiringSoon(licence.validTo)">
                <div class="licence-row__title">
                  <span class="licence-row__name">{{ localizedName }}</span>
                  <span class="licence-row__meta">
                    <code class="licence-row__key">{{ licence.key }}</code>
                    @if (!licence.validFrom && !licence.validTo) {
                      <span class="licence-chip licence-chip--perpetual">
                        {{ 'settings.licensing.dialog.perpetual' | translate }}
                      </span>
                    } @else {
                      <span class="licence-chip" [class.licence-chip--warn]="isExpiringSoon(licence.validTo)">
                        @if (licence.validFrom) {
                          <span>
                            {{ 'settings.licensing.dialog.validFrom' | translate }}
                            {{ licence.validFrom | date: 'mediumDate' }}
                          </span>
                        }
                        @if (licence.validFrom && licence.validTo) {
                          <span class="licence-chip__separator" aria-hidden="true">·</span>
                        }
                        @if (licence.validTo) {
                          <span>
                            {{ 'settings.licensing.dialog.validTo' | translate }}
                            {{ licence.validTo | date: 'mediumDate' }}
                          </span>
                        }
                      </span>
                    }
                    <button
                      zagButton
                      type="button"
                      class="licence-row__info"
                      [zagTooltip]="detailsTooltip"
                      tooltipPosition="left"
                      [attr.aria-label]="'settings.licensing.dialog.details' | translate"
                    >
                      <span zagIcon="info" aria-hidden="true"></span>
                    </button>
                  </span>
                </div>
                @if (localizedDescription) {
                  <p class="licence-row__description" [attr.title]="localizedDescription">
                    {{ localizedDescription }}
                  </p>
                }
                <ng-template #detailsTooltip>
                  <div class="licence-tooltip">
                    @if (licence.features.length > 0) {
                      <div class="licence-tooltip__section">
                        <span class="licence-tooltip__label">{{ 'settings.licensing.dialog.features' | translate }}</span>
                        <ul class="licence-tooltip__features">
                          @for (feature of licence.features; track feature.key) {
                            <li>
                              <code class="licence-tooltip__code">
                                {{ (feature.translations | zagTranslated: 'name' : feature.name) ?? feature.key }}
                              </code>
                            </li>
                          }
                        </ul>
                      </div>
                    }
                  </div>
                </ng-template>
              </li>
            }
          </ul>
        }
      </div>

      <div class="licences-dialog__footer">
        <p class="licences-dialog__cta-text">{{ 'settings.licensing.dialog.cta.text' | translate }}</p>
        <a class="licences-dialog__cta-link" href="mailto:support@smartcapacity.io">
          {{ 'settings.licensing.dialog.cta.button' | translate }}
        </a>
      </div>
    </div>
  `,
  styleUrl: './active-licences-dialog.component.scss',
  host: { class: 'active-licences-dialog-host' },
})
export class ActiveLicencesDialogComponent {
  private readonly dialogRef = inject(DialogRef<void, ActiveLicencesDialogData>);

  protected readonly licences = computed<readonly ActiveLicenceView[]>(() =>
    (this.dialogRef.config.data?.licences ?? []).filter((licence: ActiveLicenceView) => licence.features.length > 0)
  );
  protected readonly loading = computed<boolean>(() => this.dialogRef.config.data?.loading ?? false);

  protected onClose(): void {
    this.dialogRef.close();
  }

  protected isExpiringSoon(validTo: string | null): boolean {
    if (!validTo) return false;
    const expiryMs = Date.parse(validTo);
    if (Number.isNaN(expiryMs)) return false;
    const delta = expiryMs - Date.now();
    return delta > 0 && delta <= EXPIRY_SOON_THRESHOLD_MS;
  }
}
